import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom'
import ProjectDetail from '../_components/Simulation/ProjectDetail'
import * as simulationSSE from '../_components/Simulation/SimulationSSE'
import { getOneSimulationDetails } from '../requests'
import styles from '../_css/Simulation.module.css'
import { useSelector } from 'react-redux'




const VALID_TABS = ['persona', 'queries', 'response', 'competitive', 'crossplatform', 'run', 'download']

/** Remember last opened simulation per project so /details/:projectId/... works after refresh (no location.state). */
const SIM_ID_STORAGE_PREFIX = 'mediplusve_sim_for_project_'

function mapApiProjectToUi(apiProject) {

  if (!apiProject) return null
  const simulationsCount = apiProject._count?.ai_simulations ?? apiProject.ai_simulations?.length ?? 0
  const funnelStage = apiProject.simulation_funnel_stage
  const funnel = funnelStage && String(funnelStage).trim() ? [funnelStage] : []
  const sims = apiProject.ai_simulations ?? []
  const maxId = sims.length
    ? sims.reduce((max, current) => (current.id > max.id ? current : max))
    : null
  const maxIdStatus = maxId?.status ?? 10
  const maxIdAISimulationData = maxId?.aiSimulationRuns?.[0] ?? null
  const mentionRate = maxIdAISimulationData?.client_mention_rate ?? null
  const sov = maxIdAISimulationData?.client_share_of_voice ?? null
  return {
    id: apiProject.id,
    name: apiProject.name ?? '',
    brand: apiProject.brand?.name ?? '',
    brandId: apiProject.brand?.id ?? apiProject.brand_id ?? null,
    market: apiProject.country ?? '',
    country: apiProject.country ?? '',
    category: '',
    funnel,
    competitors: [],
    simulations: simulationsCount,
    lastRun: apiProject.createdAt ?? null,
    status: maxIdStatus,
    latestSimulationId: maxId?.id ?? null,
    mentionRate,
    sov,
    cost: 0,
  }
}

/** Map GET /simulation/details payload for header row (project + simulation meta). */
function mapSimulationDetailToProject(data) {
  if (!data) return null
  const p = data.project ?? {}
  const brand = p.brand ?? {}
  const funnelStage = data.funnel_stage
  const funnel = funnelStage && String(funnelStage).trim() ? [funnelStage] : []
  const runs = data.aiSimulationRuns
  return {
    id: p.id,
    name: p.name ?? '',
    brand: brand.name ?? '',
    brandId: brand.id ?? null,
    market: '',
    country: '',
    category: '',
    funnel,
    competitors: [],
    simulations: Array.isArray(runs) ? runs.length : 0,
    lastRun: data.updatedAt ?? data.createdAt ?? null,
    status: data.status ?? 10,
    latestSimulationId: data.id,
    mentionRate: null,
    sov: null,
    cost: 0,
  }
}

const SimulationDetail = () => {
  const location = useLocation()
  const context = useSelector((state) => state.context)
  const { project: initialProject, simulationId: stateSimulationId } = location.state ?? {}
  const { id, tab } = useParams()
  const navigate = useNavigate()
  const [project, setProject] = useState(initialProject ? mapApiProjectToUi(initialProject) : null)
  const [simulationDetailPayload, setSimulationDetailPayload] = useState(null)
  const [simulationDetailError, setSimulationDetailError] = useState(null)
  const [loading, setLoading] = useState(true)
  

  // Redirect to default tab when no tab or invalid tab in URL
  useEffect(() => {
    if (!id) return
    if (!tab || !VALID_TABS.includes(tab)) {
      navigate(
        { pathname: `/simulations/details/${id}/persona`, search: location.search },
        { replace: true }
      )
    }
  }, [id, tab, navigate, location.search])

  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setSimulationDetailError(null)

    getOneSimulationDetails(id, false)
      .then((res) => {
        if (cancelled) return
        if (res?.status >= 400) {
          setSimulationDetailPayload(null)
          setProject(null)
          setSimulationDetailError(res?.data?.message ?? 'Failed to load simulation')
          return
        }
        setSimulationDetailError(null)
        const data = res?.data ?? res
        setSimulationDetailPayload(data)
        setProject(mapSimulationDetailToProject(data))
        const pid = data.project?.id
        if (pid != null && data.id != null) {
          sessionStorage.setItem(SIM_ID_STORAGE_PREFIX + String(pid), String(data.id))
        }
      })
      .catch((err) => {
        if (cancelled) return
        setSimulationDetailPayload(null)
        setProject(null)
        setSimulationDetailError(err?.response?.data?.message ?? 'Failed to load simulation')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [id])

  const refetchSimulationDetails = useCallback(() => {
    const sid =
      simulationDetailPayload?.id ??
      (id ? sessionStorage.getItem(SIM_ID_STORAGE_PREFIX + id) : null) ??
      stateSimulationId
    if (sid == null || sid === '') return Promise.resolve()
    return getOneSimulationDetails(sid, false).then((res) => {
      if (res?.status >= 400) return
      const data = res?.data ?? res
      setSimulationDetailPayload(data)
      setProject(mapSimulationDetailToProject(data))
    })
  }, [simulationDetailPayload?.id, id, stateSimulationId])

  const refetchRef = useRef(refetchSimulationDetails)
  refetchRef.current = refetchSimulationDetails

  // The completion notice only carries a status. Reload the simulation so Perplexity,
  // Claude, and Gemini pick up the model rows saved in that same update.
  useEffect(() => {
    const sid = simulationDetailPayload?.id
    const status = Number(simulationDetailPayload?.status)
    if (sid == null || !simulationSSE.isNonTerminalStatus(status)) return undefined

    let cancelled = false
    simulationSSE.connect(sid, {
      hydrateFromApi: false,
      onTerminal: () => {
        if (!cancelled) refetchRef.current()
      },
    })
    return () => {
      cancelled = true
      simulationSSE.disconnect(sid)
    }
  }, [simulationDetailPayload?.id, simulationDetailPayload?.status])

  if (loading) {
    return (
      <>
        {/* <Header userType={role} headerTitle="Simulations" /> */}
        <div className={styles.page}>
          <div style={{ padding: 24, color: '#999999' }}>Loading...</div>
        </div>
      </>
    )
  }

  if (!project) {
    return (
      <>
        {/* <Header userType={role} headerTitle="Simulations" /> */}
        <div className={styles.page}>
          <div style={{ padding: 24, color: '#999999' }}>
            {simulationDetailError ?? 'Project not found.'}
          </div>
          <button
            type="button"
            onClick={() => navigate('/simulations')}
            style={{
              padding: '8px 14px',
              borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.12)',
              background: '#fff',
              color: '#6b7280',
              fontSize: 13,
              cursor: 'pointer',
              marginTop: 12,
            }}
          >
            ← Back to Simulations
          </button>
        </div>
      </>
    )
  }

  const tabFromUrl = tab && VALID_TABS.includes(tab) ? tab : 'persona'

  if (!context.simulation_id && !id) {
    return <Navigate to="/brands" />
  }

  return (
    <div className={styles.page}>
      <ProjectDetail
        project={project}
        onBack={() => navigate('/simulations')}
        tabFromUrl={tabFromUrl}
        simulationDetailPayload={simulationDetailPayload}
        forcedSimulationId={simulationDetailPayload?.id ?? null}
        onSimulationRefetch={refetchSimulationDetails}
        onNavigateToTab={(nextTab) =>
          navigate({ pathname: `/simulations/details/${id}/${nextTab}`, search: location.search })
        }
      />
    </div>
  )
}

export default SimulationDetail
